import React from 'react';
import moment from 'moment-timezone';
import i18next from 'i18next';
import SessionPresenter from './SessionPresenter';
import FilePresenter from './FilePresenter';

class PatientPresenter {
  constructor(patientData) {
    this.uuid = patientData.uuid;
    this.fullName = patientData.full_name;
    this.gender = patientData.gender;
    this.email = patientData.email;
    this.mobileNumber = patientData.mobile_number;
    this.externalId = patientData.external_id;
    this.dob = patientData.dob;
    this.location = patientData.location;
    this.link = patientData.link;
    this.comments = patientData.comments;
    this.completedSessionsCount = patientData.completed_sessions_count;
    this.completedCallsCount = patientData.completed_calls_count;
    this.lastSessionCompletedAt = patientData.last_session_completed_at;
    this.civilStatus = patientData.civil_status;
    this.occupation = patientData.occupation;
    this.religion = patientData.religion;
    this.weight = patientData.weight;
    this.height = patientData.height;
    this.bloodType = patientData.blood_type;
    this.bmi = patientData.bmi;
    this.age = patientData.age;
    
    // Store sessions if provided in the patient data
    this.sessions = patientData.sessions ? patientData.sessions
      .map(session => new SessionPresenter(session))
      .sort((a, b) => {
        // Sort by completed_at date, most recent first
        const dateA = a.session?.completed_at || a.session?.started_at;
        const dateB = b.session?.completed_at || b.session?.started_at;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateB) - new Date(dateA);
      }) : [];

    this.files = patientData.files ? patientData.files.map(file => new FilePresenter(file)) : [];
  }

  getUUID() {
    return this.uuid;
  }

  getFullName() {
    return this.fullName;
  }

  getGender() {
    if (!this.gender) return null;
    return i18next.t(`patient_form.fields.gender_${this.gender}`);
  }

  getGenderSubject() {
    if (!this.gender) return null;
    const age = parseInt(this.getAge());
    const suffix = !isNaN(age) && age < 12 ? '_child' : '_subject';
    return i18next.t(`patient_form.fields.gender_${this.gender}${suffix}`);
  }

  getGenderValue() {
    return this.gender;
  }

  getEmail() {
    return this.email;
  }

  getMobileNumber() {
    return this.mobileNumber;
  }

  getExternalId() {
    return this.externalId;
  }

  getDOB() {
    return this.dob;
  }

  // Returns the date of birth in a formatted string.
  getDOBFormatted(format = 'D MMM YYYY', locale = 'es') {
    if (!this.dob) return this.getAgeDisplay() || '';

    const dobMoment = moment.utc(this.dob).local().locale(locale);
    const formattedDate = dobMoment.format(format);
    return `${this.getAgeDisplay()} (${formattedDate})`;
  }

  getAge() {
    if (this.dob) {
      const dobMoment = moment.utc(this.dob).local();
      const age = moment.utc().diff(dobMoment, 'years');
      return age.toString();
    }
    if (this.age) {
      // Extract just the number from the age string
      const ageMatch = this.age.toString().match(/^(\d+)/);
      return ageMatch ? ageMatch[1] : this.age;
    }
    return '';
  }

  getAgeDisplay() {
    if (this.dob) {
      const dobMoment = moment.utc(this.dob).local();
      return dobMoment.fromNow(true); // e.g., "43 años"
    }
    if (this.age) {
      return moment.duration(parseInt(this.age), 'years').humanize();
    }
    return '';
  }

  getDemographics() {
    const parts = [];
    if (this.gender) {
      parts.push(this.getGenderSubject());
    }
    if (this.dob) {
      parts.push(this.getAgeDisplay());
    }
    if (parts.length < 2 && this.location) {
      parts.push(this.location);
    }
    return parts.slice(0, 2).join(', ');
  }

  getLocation() {
    return this.location;
  }

  getLink() {
    return this.link;
  }

  getLinkElement() {
    if (!this.link) return '';

    const truncated =
      this.link.length > 50
        ? `${this.link.slice(0, 47)}...`
        : this.link;

    return (
      <a
        href={this.link}
        target="_blank"
        rel="noopener noreferrer"
        title={this.link}
      >
        {truncated}
      </a>
    );
  }

  getComments() {
    return this.comments;
  }

  getCompletedSessionsCount() {
    return this.completedSessionsCount;
  }

  getCompletedCallsCount() {
    return this.completedCallsCount;
  }

  getCivilStatus() {
    return this.civilStatus;
  }

  getOccupation() {
    return this.occupation;
  }

  getReligion() {
    return this.religion;
  }

  getWeight() {
    return this.weight;
  }

  getHeight() {
    return this.height;
  }

  getBloodType() {
    return this.bloodType;
  }

  getBMI() {
    return this.bmi;
  }

  getWeightDisplay() {
    if (!this.weight) return '';
    return this.weight.toString().toLowerCase().includes('kg') ? this.weight : `${this.weight} kg`;
  }

  getHeightDisplay() {
    if (!this.height) return '';
    return this.height.toString().toLowerCase().includes('cm') ? this.height : `${this.height} cm`;
  }

  getCalculatedBMI() {
    if (this.bmi) return this.bmi;
    
    if (this.height && this.weight) {
      const heightInMeters = parseFloat(this.height) / 100;
      const weightInKg = parseFloat(this.weight);
      if (!isNaN(heightInMeters) && !isNaN(weightInKg) && heightInMeters > 0) {
        const bmi = weightInKg / (heightInMeters * heightInMeters);
        return bmi.toFixed(1);
      }
    }
    return '';
  }

  // New methods for accessing patient sessions and medications
  getSessions() {
    return this.sessions;
  }

  getCompletedDoctorNotes() {
    // Return all completed doctor notes from all sessions, sorted by date (most recent first)
    return this.sessions
      .flatMap(sessionPresenter => sessionPresenter.getCompletedDoctorNotes())
      .sort((a, b) => {
        const dateA = a.note?.completed_at || a.note?.created_at;
        const dateB = b.note?.completed_at || b.note?.created_at;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateB) - new Date(dateA);
      });
  }

  getLastCompletedDoctorNote() {
    const completedNotes = this.getCompletedDoctorNotes();
    return completedNotes.length > 0 ? completedNotes[0] : null;
  }

  getLastDoctorNote() {
    // For backward compatibility, return the most recent completed doctor note
    return this.getLastCompletedDoctorNote();
  }

  getLastMedications() {
    const lastDoctorNote = this.getLastDoctorNote();
    if (!lastDoctorNote || !lastDoctorNote.isValid()) {
      return [];
    }
    const medications = lastDoctorNote.getMedications() || [];
    return medications;
  }

  hasSessions() {
    return this.sessions.length > 0;
  }

  hasMedications() {
    return this.getLastMedications().length > 0;
  }

  getFiles() {
    return this.files;
  }
}

export default PatientPresenter;